import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from '@mui/material';
import type { VaultFileAccount } from '@/store/accountsApi';
import { DataRangeFilterEnum } from '@/constants/dates';
import { useLocalization } from '@/hooks/useLocalization';
import {
  advisorFeedDateFilterOptions,
  advisorFeedDateTranslationKey,
  formatVaultAccountOption,
} from './advisorFeedTab';

const ALL_ACCOUNTS_MENU_VALUE = -1;

function accountIdsFromSelectValue(
  value: string | readonly number[],
): number[] {
  const tokens = typeof value === 'string' ? value.split(',') : value;
  return tokens.map((token) => Number(token));
}

function selectedAccountLabels(
  selectedIds: readonly number[],
  accounts: readonly VaultFileAccount[],
): string {
  return selectedIds
    .map((id) => {
      const account = accounts.find((item) => item.WealthAccountId === id);
      return account === undefined
        ? String(id)
        : formatVaultAccountOption(account);
    })
    .join(', ');
}

interface AdvisorFeedAccountSelectProps {
  idPrefix: string;
  accounts: readonly VaultFileAccount[];
  selectedAccountIds: readonly number[];
  onChange: (accountIds: number[]) => void;
  loadFailed?: boolean;
}

export const AdvisorFeedAccountSelect = ({
  idPrefix,
  accounts,
  selectedAccountIds,
  onChange,
  loadFailed = false,
}: AdvisorFeedAccountSelectProps) => {
  const { t } = useLocalization();
  const labelId = `${idPrefix}-account-select-label`;
  const selectId = `${idPrefix}-account-select`;
  const label = t('account', 'Account');
  const hasAccounts = accounts.length > 0;

  const handleChange = (event: SelectChangeEvent<number[]>) => {
    const next = accountIdsFromSelectValue(event.target.value);
    if (next.length === 0 || next.includes(ALL_ACCOUNTS_MENU_VALUE)) {
      onChange([]);
      return;
    }
    onChange(next);
  };

  return (
    <FormControl
      sx={{ minWidth: 180 }}
      size='small'
    >
      <InputLabel
        id={labelId}
        shrink
      >
        {label}
      </InputLabel>
      <Select<number[]>
        labelId={labelId}
        id={selectId}
        multiple
        value={[...selectedAccountIds]}
        label={label}
        onChange={handleChange}
        displayEmpty
        disabled={!hasAccounts || loadFailed}
        renderValue={(selected) => {
          if (loadFailed) {
            return t(
              'documentVault.feedAccountsLoadError',
              'Unable to load accounts.',
            );
          }
          if (!hasAccounts) {
            return t('noAccountsFound', 'No accounts found');
          }
          if (selected.length === 0) {
            return t('viewingTag.allAccounts', 'All Accounts');
          }
          return selectedAccountLabels(selected, accounts);
        }}
      >
        {hasAccounts ? (
          <MenuItem value={ALL_ACCOUNTS_MENU_VALUE}>
            {t('viewingTag.allAccounts', 'All Accounts')}
          </MenuItem>
        ) : (
          <MenuItem
            value={ALL_ACCOUNTS_MENU_VALUE}
            disabled
          >
            {t('noAccountsFound', 'No accounts found')}
          </MenuItem>
        )}
        {accounts.map((account) => (
          <MenuItem
            key={account.WealthAccountId}
            value={account.WealthAccountId}
          >
            {formatVaultAccountOption(account)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface AdvisorFeedDateSelectProps {
  idPrefix: string;
  value: DataRangeFilterEnum;
  onChange: (filter: DataRangeFilterEnum) => void;
}

export const AdvisorFeedDateSelect = ({
  idPrefix,
  value,
  onChange,
}: AdvisorFeedDateSelectProps) => {
  const { t } = useLocalization();
  const labelId = `${idPrefix}-date-select-label`;
  const label = t('grid.statementDate', 'Statement Date');

  return (
    <FormControl
      sx={{ minWidth: 180 }}
      size='small'
    >
      <InputLabel
        id={labelId}
        shrink
      >
        {label}
      </InputLabel>
      <Select
        labelId={labelId}
        id={`${idPrefix}-date-select`}
        value={value}
        label={label}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        {advisorFeedDateFilterOptions.map((option) => (
          <MenuItem
            key={option.filter}
            value={option.filter}
          >
            {t(advisorFeedDateTranslationKey(option.filter), option.label)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
